import { Link } from 'react-router';
import {
  Monitor,
  Cpu,
  ShieldCheck,
  BarChart3,
  Building2,
  ArrowRight,
  CheckCircle2,
} from 'lucide-react';
import { Section } from '../components/Section';

const detectionCategories = [
  'PII (names, emails, phone numbers, national IDs)',
  'PCI (credit cards, bank accounts, sort codes)',
  'Credentials (API keys, tokens, connection strings)',
  'Confidential IP (source code, internal docs, trade secrets)',
];

const policyFeatures = [
  'Per-category actions (block PII, warn on source code, allow public data)',
  'Exclusion lists for trusted domains',
  'Package security policies — block imports with known CVEs',
  'AI service allow/deny lists',
];

const dashboardFeatures = [
  'Live event timeline with category, severity, and action taken',
  'Per-device and per-user activity views',
  'Trend analysis and detection volume charts',
  'Export and API access for SIEM integration',
];

const enterpriseFeatures = [
  'Multi-tenant isolation: dedicated databases per org',
  'GDPR, UK DPA 2018, CCPA/CPRA compliance',
  'UK data residency',
  'Zero-trust auth with OPA-enforced permissions',
  'No raw content stored — metadata only',
];

interface FeatureSectionProps {
  icon: React.ElementType;
  title: string;
  description: string;
  items: string[];
  footnote?: string;
  reversed?: boolean;
}

function FeatureSection({
  icon: Icon,
  title,
  description,
  items,
  footnote,
  reversed,
}: FeatureSectionProps) {
  return (
    <Section>
      <div
        className={`grid items-center gap-12 md:grid-cols-2 ${reversed ? 'md:[&>:first-child]:order-2' : ''}`}
      >
        {/* Text side */}
        <div>
          <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-red-subtle">
            <Icon size={24} className="text-red-accent" />
          </div>
          <h3 className="mb-4 text-2xl font-bold text-text-primary md:text-3xl">{title}</h3>
          <p className="mb-6 text-text-secondary leading-relaxed">{description}</p>
          <ul className="space-y-3">
            {items.map((item) => (
              <li key={item} className="flex items-start gap-3">
                <CheckCircle2
                  size={18}
                  className="mt-0.5 shrink-0 text-green-ok"
                />
                <span className="text-sm text-text-secondary">{item}</span>
              </li>
            ))}
          </ul>
          {footnote && (
            <p className="mt-6 rounded-lg border border-border bg-surface-raised p-4 text-sm leading-relaxed text-text-secondary">
              {footnote}
            </p>
          )}
        </div>

        {/* Visual side */}
        <div className="flex items-center justify-center">
          <div className="relative h-64 w-full max-w-md overflow-hidden rounded-2xl border border-border bg-surface p-1">
            {/* Decorative geometric pattern */}
            <div className="absolute inset-0 bg-gradient-to-br from-red-accent/5 via-transparent to-cyan-info/5" />
            <div className="relative flex h-full items-center justify-center">
              <div className="grid grid-cols-3 gap-3">
                {Array.from({ length: 9 }).map((_, i) => (
                  <div
                    key={i}
                    className="h-12 w-12 rounded-lg border border-border/50 bg-surface-raised/50 transition-colors duration-500"
                    style={{
                      opacity: 0.3 + (i % 3) * 0.25,
                      animationDelay: `${i * 0.15}s`,
                    }}
                  />
                ))}
              </div>
              <div className="absolute inset-0 flex items-center justify-center">
                <Icon size={48} className="text-red-accent/40" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </Section>
  );
}

export function Features() {
  return (
    <>
      {/* Hero */}
      <section className="relative overflow-hidden px-6 pb-16 pt-20 md:pb-20 md:pt-28">
        <div className="pointer-events-none absolute -top-32 right-1/4 h-[500px] w-[500px] rounded-full bg-red-accent/5 blur-3xl" />
        <div className="relative mx-auto max-w-3xl text-center">
          <h1 className="animate-fade-in-up text-4xl font-extrabold leading-tight tracking-tight md:text-6xl">
            Built for{' '}
            <span className="bg-gradient-to-r from-red-accent to-cyan-info bg-clip-text text-transparent">
              real protection
            </span>
          </h1>
          <p className="animate-fade-in-up animate-delay-100 mx-auto mt-6 max-w-2xl text-lg text-text-secondary">
            Redbax works at the OS level to intercept, classify and control data before it reaches
            an AI service — across browsers, desktop apps and IDEs. Everything is inspected locally on
            the endpoint. No cloud proxy. No browser extensions.
          </p>
        </div>
      </section>

      {/* Feature sections */}
      <FeatureSection
        icon={Cpu}
        title="Endpoint Detection Engine"
        description="Runs natively on Windows and Linux. Intercepts prompts and file uploads at the OS level before they reach AI services."
        items={detectionCategories}
        footnote="Powered by a fine-tuned NER model with pattern matching fallbacks. Runs locally — your data never leaves the device for classification."
      />

      <div className="mx-auto max-w-7xl px-6">
        <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent" />
      </div>

      <FeatureSection
        icon={ShieldCheck}
        title="Policy & Rules Engine"
        description="Granular control over what gets blocked, warned, or allowed. Configure policies by data category, team, or service."
        items={policyFeatures}
        reversed
      />

      <div className="mx-auto max-w-7xl px-6">
        <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent" />
      </div>

      <FeatureSection
        icon={BarChart3}
        title="SOC Dashboard"
        description="Full visibility into what your team is sharing with AI tools. Real-time monitoring with actionable insights."
        items={dashboardFeatures}
      />

      <div className="mx-auto max-w-7xl px-6">
        <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent" />
      </div>

      <FeatureSection
        icon={Building2}
        title="Enterprise-Ready"
        description="Built for organisations that take data security seriously. Multi-tenant architecture with full regulatory compliance."
        items={enterpriseFeatures}
        reversed
      />

      {/* CTA */}
      <section className="bg-gradient-to-r from-red-subtle to-surface px-6 py-20 md:py-24">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="text-3xl font-bold text-text-primary md:text-4xl">
            Ready to protect your data?
          </h2>
          <p className="mt-4 text-lg text-text-secondary">
            Be the first to know when we launch.
          </p>
          <Link
            to="/contact"
            className="mt-8 inline-flex items-center gap-2 rounded-lg bg-red-accent px-8 py-3.5 text-base font-semibold text-white transition-colors hover:bg-red-hover"
          >
            Register Interest
            <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </>
  );
}
